import { Controller } from "@hotwired/stimulus"
import { initializePaddle } from "@paddle/paddle-js"

// Paddle plan picker: localized prices via Paddle.PricePreview, Subscribe via
// Paddle.Checkout.open (one-page overlay). Prices are shown exactly as Paddle
// returns them (formattedTotals.total): no math, no reformatting.
//
// Someone who already subscribes never opens a second checkout (that would leave two live
// subscriptions and bill them twice): "Switch plan" posts to changeUrl, which updates their existing
// Paddle subscription on the server. While a payment is overdue (locked) plan buttons are off.
//
// Values: environment/token (sandbox|production + client-side token, from
// PaddleConfig), country (ISO code, omitted when unknown so Paddle detects it
// from the visitor's IP), email, accountId, successUrl.
export default class extends Controller {
  static targets = ["monthlyButton", "annualButton", "price", "period", "reference", "subscribe", "card", "error"]
  static values = {
    environment: String,
    token: String,
    country: String,
    email: String,
    accountId: Number,
    successUrl: String,
    changeUrl: String,
    locked: Boolean,
    interval: { type: String, default: "year" }
  }

  async connect() {
    this.#render()
    try {
      this.paddle = await initializePaddle({ environment: this.environmentValue, token: this.tokenValue })
      if (!this.paddle) throw new Error("Paddle failed to initialize")
      await this.#loadPrices()
    } catch (error) {
      console.error("[paddle]", error)
      this.#showError("Prices are unavailable right now. Please refresh, or contact support.")
    }
  }

  showMonthly() { this.intervalValue = "month"; this.#render() }
  showAnnual() { this.intervalValue = "year"; this.#render() }

  subscribe(event) {
    const priceId = event.currentTarget.dataset[this.intervalValue === "year" ? "yearPriceId" : "monthPriceId"]
    // Changing plan is a server request and does not need Paddle.js, so it works even if that failed to load
    if (this.changeUrlValue) return this.#changePlan(event.currentTarget, priceId)

    if (!this.paddle) return

    this.paddle.Checkout.open({
      items: [{ priceId, quantity: 1 }],
      ...(this.emailValue && { customer: { email: this.emailValue } }),
      customData: { account_id: String(this.accountIdValue) },
      settings: {
        displayMode: "overlay",
        variant: "one-page",
        successUrl: this.successUrlValue
      }
    })
  }

  #changePlan(button, priceId) {
    const name = button.closest('[data-paddle-checkout-target~="card"]')?.querySelector(".fw-semibold")?.textContent?.trim() || "this plan"
    const period = this.intervalValue === "year" ? "yearly" : "monthly"
    if (!window.confirm(`Switch to ${name} (${period})? Paddle will bill or credit the difference for the rest of this billing period.`)) return

    // A plain form post (not fetch): the server redirects back here with the result.
    const form = document.createElement("form")
    form.method = "post"
    form.action = this.changeUrlValue
    const add = (name, value) => {
      const input = document.createElement("input")
      input.type = "hidden"
      input.name = name
      input.value = value
      form.append(input)
    }
    add("authenticity_token", document.querySelector('meta[name="csrf-token"]')?.content || "")
    add("price_id", priceId)
    document.body.append(form)
    form.submit()
  }

  async #loadPrices() {
    const priceIds = [...new Set(this.priceTargets.map((el) => el.dataset.priceId))]
    const items = priceIds.map((priceId) => ({ priceId, quantity: 1 }))
    const request = { items }
    if (this.countryValue) request.address = { countryCode: this.countryValue }

    const preview = await this.paddle.PricePreview(request)
    const lines = preview.data.details.lineItems
    const totals = new Map(lines.map((li) => [li.price.id, li.formattedTotals.subtotal]))
    const raw = new Map(lines.map((li) => [li.price.id, li.totals.subtotal]))
    const currency = preview.data.currencyCode

    this.priceTargets.forEach((el) => {
      const id = el.dataset.priceId
      el.textContent = el.dataset.perMonth === "true"
        ? this.#perMonth(raw.get(id), currency)
        : (totals.get(id) ?? "")
    })
    // Yearly cards: the annual total under the per-month figure, as Paddle formatted it.
    this.referenceTargets.forEach((el) => {
      const annual = totals.get(el.dataset.priceId)
      el.textContent = annual ? `billed ${annual} / year` : ""
    })
    this.pricesReady = true
    this.#syncCards()
  }

  // Annual price spread over 12 months, rounded to whole units like the
  // pricing page. The only figure here not taken verbatim from Paddle, so it
  // is formatted in the same currency (INR with Indian grouping).
  #perMonth(minorUnits, currency) {
    const locale = currency === "INR" ? "en-IN" : "en-US"
    const format = new Intl.NumberFormat(locale, { style: "currency", currency, maximumFractionDigits: 0 })
    const fractionDigits = new Intl.NumberFormat(locale, { style: "currency", currency }).resolvedOptions().maximumFractionDigits
    const perMonth = Math.round(Number(minorUnits) / 10 ** fractionDigits / 12)
    return Number.isFinite(perMonth) ? format.format(perMonth) : ""
  }

  #render() {
    const annual = this.intervalValue === "year"
    this.monthlyButtonTarget.classList.toggle("active", !annual)
    this.annualButtonTarget.classList.toggle("active", annual)
    this.priceTargets.forEach((el) => el.classList.toggle("d-none", el.dataset.interval !== this.intervalValue))
    this.periodTargets.forEach((el) => el.classList.toggle("d-none", el.dataset.interval !== this.intervalValue))
    this.#syncCards()
  }

  // "Current plan" only applies to the interval being viewed: a monthly
  // subscriber sees Current on the Monthly view, not the Yearly one. The dark
  // card is the current one, else the featured one.
  #syncCards() {
    const isCurrent = (card) =>
      card.dataset.currentPlan === "true" &&
      (card.dataset.currentInterval === "any" || card.dataset.currentInterval === this.intervalValue)
    const anyCurrent = this.cardTargets.some(isCurrent)

    this.cardTargets.forEach((card) => {
      const current = isCurrent(card)
      const featured = card.dataset.featured === "true"
      card.classList.toggle("is-dark", anyCurrent ? current : featured)
      card.querySelector('[data-badge="current"]')?.classList.toggle("d-none", !current)
      card.querySelector('[data-badge="popular"]')?.classList.toggle("d-none", current || !featured)

      const button = card.querySelector('[data-paddle-checkout-target~="subscribe"]')
      if (button) {
        button.textContent = current ? "Current plan"
          : this.lockedValue ? "Update payment first"
          : this.changeUrlValue ? "Switch plan" : "Subscribe"
        button.disabled = current || this.lockedValue || (!this.changeUrlValue && !this.pricesReady)
      }
    })
  }

  #showError(message) {
    this.errorTarget.textContent = message
    this.errorTarget.classList.remove("d-none")
  }
}
